import {
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Grid,
	If,
	makeClassName,
	Row,
	StyleProps,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { StudentDataConsent } from "../../../../Utilities";
import { StudentDataConsentFormat } from "../../../Format";
import styles from "./styles.module.css";

export type StudentDataConsentSummaryProps = {
	studentDataConsent: Pick<
		StudentDataConsent,
		| "allowPublishVisualMediaOfStudentGeneral"
		| "allowPublishVisualMediaOfStudentForPromotion"
		| "allowPublishStudentWorkGeneral"
		| "allowPublishVisualMediaOfGuardianForPromotion"
		| "allowShareInformationWithPearsonEdexcel"
		| "allowShareInformationWithIbOrganization"
		| "allowShareInformationWithOnlinePlatforms"
		| "allowBirthdayInCalendar"
		| "allowSendNewsletters"
		| "allowSendCalendarEvents"
	>;
} & Partial<ClassNameProps & StyleProps>;

export function StudentDataConsentSummary({ studentDataConsent, className, style }: StudentDataConsentSummaryProps) {
	const anyAllowed =
		studentDataConsent.allowPublishVisualMediaOfStudentGeneral ||
		studentDataConsent.allowPublishVisualMediaOfStudentForPromotion ||
		studentDataConsent.allowPublishStudentWorkGeneral ||
		studentDataConsent.allowPublishVisualMediaOfGuardianForPromotion ||
		studentDataConsent.allowShareInformationWithPearsonEdexcel ||
		studentDataConsent.allowShareInformationWithIbOrganization ||
		studentDataConsent.allowShareInformationWithOnlinePlatforms ||
		studentDataConsent.allowBirthdayInCalendar ||
		studentDataConsent.allowSendNewsletters ||
		studentDataConsent.allowSendCalendarEvents;
	const anyDenied =
		!studentDataConsent.allowPublishVisualMediaOfStudentGeneral ||
		!studentDataConsent.allowPublishVisualMediaOfStudentForPromotion ||
		!studentDataConsent.allowPublishStudentWorkGeneral ||
		!studentDataConsent.allowPublishVisualMediaOfGuardianForPromotion ||
		!studentDataConsent.allowShareInformationWithPearsonEdexcel ||
		!studentDataConsent.allowShareInformationWithIbOrganization ||
		!studentDataConsent.allowShareInformationWithOnlinePlatforms ||
		!studentDataConsent.allowBirthdayInCalendar ||
		!studentDataConsent.allowSendNewsletters ||
		!studentDataConsent.allowSendCalendarEvents;

	return (
		<Grid
			tracks="auto-fill"
			minTrackWidth="14rem"
			gap="1rem"
			className={makeClassName(styles["summary"], className)}
			style={style}
		>
			<Column justify="spaced-start">
				<strong>Allowed</strong>

				<Conditional>
					<If condition={!anyAllowed}>
						<Row
							justify="spaced-start"
							align="center"
							horizontalSpacing="0.5rem"
							className={styles["summary-item"] + " " + styles["summary-item--none"]}
						>
							<Icon.Prohibit /> None
						</Row>
					</If>
					<Else>
						<If condition={studentDataConsent.allowPublishVisualMediaOfStudentGeneral}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.Check color="var(--theme-colors__green)" />
								{StudentDataConsentFormat.summaryLabels.allowPublishVisualMediaOfStudentGeneral}
							</Row>
						</If>
						<If condition={studentDataConsent.allowPublishVisualMediaOfStudentForPromotion}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.Check color="var(--theme-colors__green)" />
								{StudentDataConsentFormat.summaryLabels.allowPublishVisualMediaOfStudentForPromotion}
							</Row>
						</If>
						<If condition={studentDataConsent.allowPublishStudentWorkGeneral}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.Check color="var(--theme-colors__green)" />
								{StudentDataConsentFormat.summaryLabels.allowPublishStudentWorkGeneral}
							</Row>
						</If>
						<If condition={studentDataConsent.allowPublishVisualMediaOfGuardianForPromotion}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.Check color="var(--theme-colors__green)" />
								{StudentDataConsentFormat.summaryLabels.allowPublishVisualMediaOfGuardianForPromotion}
							</Row>
						</If>
						<If condition={studentDataConsent.allowShareInformationWithPearsonEdexcel}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.Check color="var(--theme-colors__green)" />
								{StudentDataConsentFormat.summaryLabels.allowShareInformationWithPearsonEdexcel}
							</Row>
						</If>
						<If condition={studentDataConsent.allowShareInformationWithIbOrganization}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.Check color="var(--theme-colors__green)" />
								{StudentDataConsentFormat.summaryLabels.allowShareInformationWithIbOrganization}
							</Row>
						</If>
						<If condition={studentDataConsent.allowShareInformationWithOnlinePlatforms}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.Check color="var(--theme-colors__green)" />
								{StudentDataConsentFormat.summaryLabels.allowShareInformationWithOnlinePlatforms}
							</Row>
						</If>
						<If condition={studentDataConsent.allowBirthdayInCalendar}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.Check color="var(--theme-colors__green)" />
								{StudentDataConsentFormat.summaryLabels.allowBirthdayInCalendar}
							</Row>
						</If>
						<If condition={studentDataConsent.allowSendNewsletters}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.Check color="var(--theme-colors__green)" />
								{StudentDataConsentFormat.summaryLabels.allowSendNewsletters}
							</Row>
						</If>
						<If condition={studentDataConsent.allowSendCalendarEvents}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.Check color="var(--theme-colors__green)" />
								{StudentDataConsentFormat.summaryLabels.allowSendCalendarEvents}
							</Row>
						</If>
					</Else>
				</Conditional>
			</Column>

			<Column justify="spaced-start">
				<strong>Denied</strong>

				<Conditional>
					<If condition={!anyDenied}>
						<Row
							justify="spaced-start"
							align="center"
							horizontalSpacing="0.5rem"
							className={styles["summary-item"] + " " + styles["summary-item--none"]}
						>
							<Icon.Prohibit /> None
						</Row>
					</If>
					<Else>
						<If condition={!studentDataConsent.allowPublishVisualMediaOfStudentGeneral}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.X color="var(--theme-colors__pink)" />
								{StudentDataConsentFormat.summaryLabels.allowPublishVisualMediaOfStudentGeneral}
							</Row>
						</If>
						<If condition={!studentDataConsent.allowPublishVisualMediaOfStudentForPromotion}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.X color="var(--theme-colors__pink)" />
								{StudentDataConsentFormat.summaryLabels.allowPublishVisualMediaOfStudentForPromotion}
							</Row>
						</If>
						<If condition={!studentDataConsent.allowPublishStudentWorkGeneral}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.X color="var(--theme-colors__pink)" />
								{StudentDataConsentFormat.summaryLabels.allowPublishStudentWorkGeneral}
							</Row>
						</If>
						<If condition={!studentDataConsent.allowPublishVisualMediaOfGuardianForPromotion}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.X color="var(--theme-colors__pink)" />
								{StudentDataConsentFormat.summaryLabels.allowPublishVisualMediaOfGuardianForPromotion}
							</Row>
						</If>
						<If condition={!studentDataConsent.allowShareInformationWithPearsonEdexcel}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.X color="var(--theme-colors__pink)" />
								{StudentDataConsentFormat.summaryLabels.allowShareInformationWithPearsonEdexcel}
							</Row>
						</If>
						<If condition={!studentDataConsent.allowShareInformationWithIbOrganization}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.X color="var(--theme-colors__pink)" />
								{StudentDataConsentFormat.summaryLabels.allowShareInformationWithIbOrganization}
							</Row>
						</If>
						<If condition={!studentDataConsent.allowShareInformationWithOnlinePlatforms}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.X color="var(--theme-colors__pink)" />
								{StudentDataConsentFormat.summaryLabels.allowShareInformationWithOnlinePlatforms}
							</Row>
						</If>
						<If condition={!studentDataConsent.allowBirthdayInCalendar}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.X color="var(--theme-colors__pink)" />
								{StudentDataConsentFormat.summaryLabels.allowBirthdayInCalendar}
							</Row>
						</If>
						<If condition={!studentDataConsent.allowSendNewsletters}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.X color="var(--theme-colors__pink)" />
								{StudentDataConsentFormat.summaryLabels.allowSendNewsletters}
							</Row>
						</If>
						<If condition={!studentDataConsent.allowSendCalendarEvents}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" className={styles["summary-item"]}>
								<Icon.X color="var(--theme-colors__pink)" />
								{StudentDataConsentFormat.summaryLabels.allowSendCalendarEvents}
							</Row>
						</If>
					</Else>
				</Conditional>
			</Column>
		</Grid>
	);
}
